import { fileToBase64 } from '@ordo/ui'
import { Button } from '@ordo/ui/button'
import { Skeleton } from '@ordo/ui/skeleton'
import { WandSparkles } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { authHeaders } from '@/api/http'

export interface NoteSuggestions {
  studentFirstName: string | null
  studentLastName: string | null
  restrictedAllergens: string[]
  acceptableMilkOptionCodes: string[]
  foodsToOmit?: string | null
  suggestedSubstitutions?: string | null
  notes: string | null
}
export function SpecialDietNoteSuggestions({
  file,
  locationId,
  onApply,
}: {
  file: File | null
  locationId: string
  onApply: (suggestions: NoteSuggestions) => void
}) {
  const [message, setMessage] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const request = useRef<AbortController | null>(null)
  useEffect(() => {
    request.current?.abort()
    setMessage(null)
    setPending(false)
    return () => request.current?.abort()
  }, [file, locationId])
  return (
    <div className="space-y-3">
      <Button
        type="button"
        variant="secondary"
        disabled={!file || pending}
        onClick={async () => {
          if (!file) {
            return
          }
          const controller = new AbortController()
          request.current = controller
          setPending(true)
          setMessage(null)
          try {
            const response = await fetch('/api/special-diet/extract-note', {
              method: 'POST',
              headers: { ...authHeaders(), 'Content-Type': 'application/json' },
              signal: controller.signal,
              body: JSON.stringify({
                locationId,
                note: { fileName: file.name, contentBase64: await fileToBase64(file) },
              }),
            })
            const result = await response.json()
            if (controller.signal.aborted) {
              return
            }
            if (!response.ok) {
              throw new Error(result.message ?? 'Unable to read this note.')
            }
            if (result.suggestions) {
              onApply(result.suggestions)
              setMessage('Details added below. Review them against the note.')
            } else {
              setMessage(result.message ?? 'Enter the details manually.')
            }
          } catch (error) {
            if (!controller.signal.aborted) {
              setMessage(error instanceof Error ? error.message : 'Enter the details manually.')
            }
          } finally {
            if (!controller.signal.aborted) {
              setPending(false)
            }
          }
        }}>
        <WandSparkles className="size-4" aria-hidden="true" />
        {pending ? 'Reading note…' : 'Auto-fill from note'}
      </Button>
      {pending && <Skeleton className="h-24 w-full" />}
      {message && (
        <p className="text-sm text-muted-foreground" role="status">
          {message}
        </p>
      )}
    </div>
  )
}
